import dayjs from 'dayjs';
import { createApi } from '@reduxjs/toolkit/query/react';
import { Utils, BaseResponse } from '@gv/triage-components';

import { URL } from 'api/constants';
import { apiQuery } from 'store/query';
import { generateUrl } from 'utils/helpers';
import { HospitalTask, HospitalTaskPet, HospitalTaskClient } from 'types';

import {
  Filters,
  TasksData,
  GetRequest,
  ListRequest,
  FilterValues,
  CreateRequest,
  UpdateRequest,
  CompleteRequest,
  FilterValuesRequest,
} from './types';

const { Query, Helpers } = Utils;

const tagTypes = ['hospitalTasks'];

export const hospitalTasksApi = createApi({
  tagTypes,
  baseQuery: apiQuery,
  reducerPath: 'hospitalTasksApi',
  endpoints: (build) => ({
    complete: build.mutation<void, CompleteRequest>({
      invalidatesTags: tagTypes,
      query: (data) => ({
        data,
        method: 'patch',
        url: URL.TASKS_COMPLETE,
      }),
    }),

    create: build.mutation<HospitalTask, CreateRequest>({
      invalidatesTags: tagTypes,
      transformResponse: (response: BaseResponse<HospitalTask>) =>
        response.data,
      query: (data) => ({
        data,
        method: 'post',
        url: URL.TASKS,
      }),
    }),

    update: build.mutation<HospitalTask, UpdateRequest>({
      invalidatesTags: tagTypes,
      transformResponse: (response: BaseResponse<HospitalTask>) =>
        response.data,
      query: ({ id, ...data }) => ({
        data,
        method: 'patch',
        url: generateUrl(URL.TASK, { id }),
      }),
    }),

    get: build.query<HospitalTask, GetRequest>({
      providesTags: tagTypes,
      transformResponse: (response: BaseResponse<HospitalTask>) =>
        response.data,
      query: ({ id, hospitalId }) => ({
        method: 'get',
        url: Utils.Query.addParamsToUrl(
          generateUrl(URL.TASK, {
            id,
          }),
          { hospitalId }
        ),
      }),
    }),

    list: build.query<TasksData, ListRequest>({
      keepUnusedDataFor: 1,
      providesTags: tagTypes,
      transformResponse: (response: BaseResponse<TasksData>) => response.data,
      query: ({ search, filters, page = 1, limit = 20 }) => {
        const offset = Helpers.offset(page, limit);

        const {
          types,
          endDate,
          statuses,
          startDate,
          assigneeId,
          priorities,
          portalType,
          isCompleted,
          ...restFilters
        } = filters;

        const resultFilters: Filters = {
          ...restFilters,
        };

        if (types?.length) {
          resultFilters.types = types;
        }

        if (statuses?.length) {
          resultFilters.statuses = statuses;
        }

        if (portalType?.length) {
          resultFilters.portalType = portalType;
        }

        if (priorities?.length) {
          resultFilters.priorities = priorities;
        }

        if (assigneeId || assigneeId === null) {
          resultFilters.assigneeId = assigneeId ? +assigneeId : null;
        }

        if (isCompleted) {
          resultFilters.isCompleted = true;
        }

        if (startDate && endDate) {
          resultFilters.period = {
            to: dayjs(endDate).endOf('D').format(),
            from: dayjs(startDate).startOf('D').format(),
          };
        }

        const searchParams = {
          limit,
          search,
          offset,
          filters: JSON.stringify(resultFilters),
        };
        return {
          method: 'get',
          url: Query.addParamsToUrl(URL.TASKS, searchParams),
        };
      },
    }),
  }),
});

export const {
  useGetQuery,
  useListQuery,
  useCreateMutation,
  useUpdateMutation,
  useCompleteMutation,
} = hospitalTasksApi;

export type {
  Filters,
  TasksData,
  HospitalTask,
  FilterValues,
  CreateRequest,
  HospitalTaskPet,
  HospitalTaskClient,
  FilterValuesRequest,
};
